/**
 * 서명 편집 다이얼로그 — <b>HTML 한 가지 방식</b>.
 *
 *  initial:
 *    null/undefined  — 닫힘
 *    {}              — 새 서명 작성 모드
 *    { id, name, bodyHtml, isDefault } — 기존 서명 수정 모드
 *
 * ## 왜 «기본(명함)» 모드를 없앴나 — "명함 삽입 후 문구 수정 불가" 의 실체
 * 예전엔 «기본(회사 명함)» 과 «HTML» 두 모드가 있었고, 다시 열 때 어느 쪽으로 열지를 화면이
 * 추측했다. 그 판정이 이랬다.
 * <pre>
 *   const looksLikeCard = initial.bodyType === "BASIC" || Boolean(initial.card);
 * </pre>
 * 서버는 저장된 HTML 이 <b>명함 모양이면 값으로 역파싱</b>해서 {@code card} 를 함께 내려준다.
 * 그래서 HTML 모드에서 명함을 넣고 문구를 고쳐 저장해도, 다시 열면 {@code card} 가 딸려와
 * 기본 모드로 열렸다. 그런데 기본 모드는 저장할 때 {@code bodyHtml} 을 무시하고 {@code card}
 * 값으로 HTML 을 다시 만든다 — <b>고쳐 둔 문구가 통째로 사라진다.</b> 현장에서 "명함 넣고 나면
 * 기본으로 돌아가서 문구를 못 고친다" 고 한 것이 정확히 이 경로다.
 *
 * 모드가 하나면 그 추측 자체가 없어진다. 저장한 것이 그대로 열리고, 그대로 저장된다.
 *
 * ## 기존 서명은 그대로 열린다 (이관 불필요)
 * BASIC 으로 저장된 행도 서버가 렌더 결과를 {@code body_html} 에 넣어 두었다
 * ({@code MailSignatureService} — card 값은 body_text 에 JSON 으로 따로 보관).
 * 즉 예전 명함 서명 28건도 이미 완성된 HTML 을 갖고 있어, 여기서 열면 그 마크업이 그대로 뜬다.
 *
 * ## 왼쪽 소스 · 오른쪽 실시간 미리보기
 * 세로로 쌓으면 미리보기가 접힌 아래에 있어 스크롤해야 보인다. 명함형 서명은 450px 표라
 * 특히 잘려 보였다. 서명은 <b>모양을 보며 고치는</b> 물건이라 소스와 결과가 한 화면에 나란히
 * 있어야 한다.
 *
 * ## 서버가 최종 결정권을 가진다
 * 저장 시 새니타이즈되므로 다시 열면 정제된 소스가 보인다 — 의도된 동작이다.
 * (스크립트 등 위험한 태그는 서버가 지운다.)
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box, Button, FormControlLabel, Stack, Switch, TextField, Typography,
} from "@mui/material";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import DOMPurify from "dompurify";

import { mailApi } from "../../api/mailApi";
import { notify }  from "../../../../util/toast";
import FormModal   from "../../../../component/common/FormModal";
import { pinImageBoxes } from "./pinImageBoxes";

const HTML_PLACEHOLDER = '<div style="font-size:13px; color:#334155;">\n  <b>OOO</b> / 주임<br/>\n  Onnme Inc.\n</div>';

export default function SignatureEditorDialog({ open, onClose, initial, onSaved }) {
    const [name, setName]           = useState("");
    const [bodyHtml, setBodyHtml]   = useState("");
    const [isDefault, setIsDefault] = useState(false);
    const [saving, setSaving]       = useState(false);
    const [uploading, setUploading] = useState(false);

    const htmlFieldRef = useRef(null);   // <img> 태그를 커서 위치에 끼워 넣기 위해
    const fileInputRef = useRef(null);

    const isEdit = Boolean(initial?.id);

    useEffect(() => {
        if (!open) return;
        setName(initial?.name || "");
        // 명함으로 저장된 서명도 body_html 에 렌더 결과가 들어 있어 그대로 열린다.
        setBodyHtml(initial?.bodyHtml || "");
        setIsDefault(Boolean(initial?.isDefault));
    }, [open, initial]);

    /** 미리보기 — 저장 후 실제로 박힐 HTML 과 같은 것을 보여 준다. */
    const htmlPreview = useMemo(
        () => DOMPurify.sanitize(pinImageBoxes(bodyHtml) || "", { ADD_ATTR: ["target", "rel"] }),
        [bodyHtml],
    );

    /**
     * 로고 등 이미지 넣기 → base64 data: URL 로 커서 위치에 <img> 삽입.
     *
     * <h4>왜 S3 업로드(mailApi.uploadSignatureImage)를 쓰지 않나</h4>
     * 업로드가 돌려주는 건 `/api/mail/signatures/images/{key}` 상대 URL인데,
     * 백엔드 HtmlSanitizer(Jsoup)는 base URI 없이 clean 하므로 <b>상대 URL src 를 통째로 지운다</b>.
     * 저장 버튼을 누르는 순간 이미지가 사라진다는 뜻. 살아남는 건 http(s)/data/cid 뿐이다.
     *
     * data: 로 박아 두면 이후 흐름이 전부 자동이다 —
     * 작성기에서 즉시 렌더되고, 발송 직전 백엔드 extractInlineImages 가 cid: 인라인 첨부로 바꾼다.
     *
     * <h4>용량 제한을 두지 않는 이유</h4>
     * body_html 은 PostgreSQL text — 길이 제한이 없다(값당 1GB, TOAST 압축).
     * 예전 주석이 "MySQL TEXT(64KB)" 라고 적어 40KB 상한을 걸어 두었는데, 이 프로젝트의 DB 는
     * PostgreSQL 이라 근거가 없는 제한이었다(운영 서명 최대 5,923자). 다만 base64 이미지는
     * 발송 메일 크기를 그대로 키우므로, 지나치게 큰 파일만 경고한다.
     */
    const handleUploadImage = (file) => {
        if (!file) return;
        if (!file.type?.startsWith("image/")) {
            notify("이미지 파일만 넣을 수 있습니다", "warning");
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }
        if (file.size > 1024 * 1024) {
            notify(`이미지가 ${Math.ceil(file.size / 1024)}KB 입니다. 서명은 보내는 메일마다 따라붙으니 200KB 이하를 권합니다.`, "warning");
        }

        setUploading(true);
        const reader = new FileReader();
        reader.onload = () => {
            const tag = `<img src="${reader.result}" alt="" style="max-width:100%;" />`;
            const el  = htmlFieldRef.current;
            const at  = el && typeof el.selectionStart === "number" ? el.selectionStart : bodyHtml.length;
            setBodyHtml(bodyHtml.slice(0, at) + tag + bodyHtml.slice(at));
            notify("이미지를 넣었습니다", "success");
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";  // 같은 파일 재선택 허용
        };
        reader.onerror = () => {
            notify("이미지를 읽지 못했습니다", "error");
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        };
        reader.readAsDataURL(file);
    };

    const handleSave = async () => {
        if (!name.trim())     { notify("서명 이름을 입력해주세요", "warning"); return; }
        if (!bodyHtml.trim()) { notify("서명 내용을 입력해주세요", "warning"); return; }
        setSaving(true);
        try {
            const body = { bodyType: "HTML", bodyHtml };
            if (isEdit) {
                await mailApi.updateSignature(initial.id, { name: name.trim(), ...body });
                if (isDefault && !initial.isDefault) {
                    await mailApi.setDefaultSignature(initial.id);
                }
            } else {
                const res = await mailApi.createSignature({ name: name.trim(), ...body, isDefault });
                const created = res?.data ?? res;
                if (isDefault && created?.id) {
                    try { await mailApi.setDefaultSignature(created.id); } catch { /* noop */ }
                }
            }
            notify("서명을 저장했습니다", "success");
            onSaved?.();
        } catch (e) {
            notify(e?.response?.data?.message || "서명 저장 실패", "error");
        } finally { setSaving(false); }
    };

    return (
        <FormModal
            open={open}
            onClose={onClose}
            title={isEdit ? "서명 편집" : "새 서명"}
            subtitle="여기 작성한 내용이 메일 작성/답장 시 본문 끝에 자동으로 들어갑니다."
            /* 좌우 2단이라 md(900px)로는 두 칸이 다 눌린다. 명함형 서명이 450px 표라
               미리보기 쪽만 700px 가까이 필요하다. */
            maxWidth="lg"
            onSave={handleSave}
            saving={saving}
        >
            <Stack spacing={1.5}>
                {/* 기본 서명 설정 — 맨 위. 서명 여러 개를 만드는 사람에게 "어느 것이 자동으로
                    붙는지" 는 이름 다음으로 중요한 정보라, 폼 맨 아래에 있으면 놓친다. */}
                <Box sx={{
                    px: 1.5, py: 0.8, borderRadius: 1.5,
                    bgcolor: isDefault ? "rgba(37,99,235,0.06)" : "#f8fafc",
                    border: `1px solid ${isDefault ? "#bfdbfe" : "#e2e8f0"}`,
                }}>
                    <FormControlLabel
                        sx={{ m: 0 }}
                        control={<Switch size="small" checked={isDefault}
                                         onChange={(e) => setIsDefault(e.target.checked)} />}
                        label={
                            <Box sx={{ ml: 0.5 }}>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>
                                    기본 서명으로 설정
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                                    메일 작성 화면을 열면 이 서명이 자동으로 붙습니다.
                                </Typography>
                            </Box>
                        }
                    />
                </Box>

                <TextField
                    label="서명 이름"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    placeholder="예: 영업용, 외부용 등"
                    fullWidth
                    sx={{ "& .MuiInputBase-root": { fontSize: 13 } }}
                />

                <Stack direction="row" alignItems="center" spacing={1}>
                    <Button
                        size="small"
                        startIcon={<ImageRoundedIcon sx={{ fontSize: 16 }} />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, color: "#475569" }}
                    >
                        {uploading ? "읽는 중…" : "이미지 넣기"}
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => handleUploadImage(e.target.files?.[0])}
                    />
                    <Typography sx={{ fontSize: 11, color: "#64748b", lineHeight: 1.5 }}>
                        작성한 HTML 이 그대로 적용됩니다. 스크립트 등 위험한 태그는 저장 시 제거됩니다.
                    </Typography>
                </Stack>

                {/* 좌: 소스 / 우: 실시간 미리보기.
                    좁은 화면(md 미만)에서는 세로로 쌓는다 — 두 칸을 억지로 붙이면 둘 다 못 쓴다. */}
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="stretch">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", mb: 0.5 }}>
                            HTML
                        </Typography>
                        <TextField
                            value={bodyHtml}
                            onChange={(e) => setBodyHtml(e.target.value)}
                            inputRef={htmlFieldRef}
                            multiline
                            minRows={18}
                            maxRows={18}
                            fullWidth
                            placeholder={HTML_PLACEHOLDER}
                            spellCheck={false}
                            InputProps={{
                                sx: {
                                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                                    fontSize: 12.5, lineHeight: 1.6, alignItems: "flex-start",
                                    bgcolor: "#f8fafc",
                                },
                            }}
                        />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <PreviewBox html={htmlPreview} />
                    </Box>
                </Stack>
            </Stack>
        </FormModal>
    );
}

/**
 * 미리보기 상자.
 *
 * <p>흰 바탕에 얹는 이유: 서명은 흰 메일 본문 위에 놓이는 물건이라, 회색 바탕에서 보면
 * 실제와 색이 달라 보인다. 표 테두리가 붙은 명함형일수록 차이가 크다.
 *
 * <p>높이를 소스 칸과 맞춘다. 예전엔 260px 로 묶여 있어 450px 표 명함이 잘렸고,
 * 스크롤해야 아래가 보였다 — "미리보기가 짤린다" 는 보고가 그것이다.
 */
function PreviewBox({ html, empty = "내용을 입력하면 여기에 표시됩니다." }) {
    return (
        <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", mb: 0.5 }}>
                미리보기
            </Typography>
            <Box
                sx={{
                    p: 1.5, height: 445, overflow: "auto",
                    border: "1px solid #e2e8f0", borderRadius: 1, bgcolor: "#fff",
                    fontSize: 13, color: "#334155",
                    "& img": { maxWidth: "100%", height: "auto" },
                    "& table": { maxWidth: "100%" },
                }}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                    __html: html
                        ? DOMPurify.sanitize(html, { ADD_ATTR: ["target", "rel"] })
                        : `<span style="color:#94a3b8">${empty}</span>`,
                }}
            />
        </Box>
    );
}
